/* eslint-disable */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Camera, X, Loader2, CheckCircle2, AlertTriangle, Plus, Trash2, Upload } from "lucide-react";
import { useProductCatalog } from "./SharedDashboard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const abs = (u) => (u && u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);
const fdate = (d) => (d ? new Date(d).toLocaleString("fr-FR") : "");

const useVerifyStatus = (userId) => {
  const [st, setSt] = useState(null);
  const load = () => userId && axios.get(`${API}/stock-verify/status/${userId}`).then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => { load(); }, [userId]);
  return [st, load];
};

const Result = ({ r }) => (
  <div className={`rounded-2xl p-5 text-center border-2 ${r.match ? "bg-emerald-50 border-emerald-500" : "bg-rose-50 border-rose-500"}`} data-testid="verify-result">
    {r.match ? <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-2" /> : <AlertTriangle className="w-12 h-12 text-rose-600 mx-auto mb-2" />}
    <p className={`font-black text-2xl ${r.match ? "text-emerald-700" : "text-rose-700"}`} data-testid="verify-result-text">
      {r.match ? `Quantité ${r.detected_total} — Vérifiée ✓` : `Écart : ${r.detected_total} sur la photo / ${r.expected_total} attendu(s)`}
    </p>
    <p className="text-zinc-600 text-xs mt-1">{r.match ? "Confirmé le" : "Signalé au CEO le"} {fdate(r.created_at)}</p>
    {r.detected?.length > 0 && <p className="text-zinc-700 text-xs mt-2">{r.detected.map((d) => `${d.model} × ${d.quantity}`).join(" · ")}</p>}
  </div>
);

// Stocker / NM modal: confirm reception (kind=reception) or report unsold stock (kind=unsold).
export const PhotoVerifyModal = ({ user, kind = "reception", onClose, onDone }) => {
  const [st, reload] = useVerifyStatus(user.id);
  const { imageFor } = useProductCatalog();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState(null);
  const ref = useRef();
  useEffect(() => { if (!busy) return; const t = setInterval(() => setElapsed((e) => e + 1), 1000); return () => clearInterval(t); }, [busy]);
  const pick = (e) => { const f = e.target.files?.[0]; if (f) { setFile(f); setPreview(URL.createObjectURL(f)); setResult(null); } };
  const submit = async () => {
    if (!file) return toast.error("Prenez d'abord la photo");
    const fd = new FormData(); fd.append("file", file); fd.append("kind", kind);
    setBusy(true); setElapsed(0);
    try {
      const r = await axios.post(`${API}/stock-verify/verify/${user.id}`, fd);
      setResult(r.data); reload(); onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur de vérification"); }
    finally { setBusy(false); }
  };
  const a = st?.assignment;
  const pending = kind === "reception" && st && (!a || a.status === "verified");
  const expectedItems = kind === "reception" ? (a?.items || []) : (user.vape_models || []).map((m) => ({ model: m.name, quantity: Math.max(0, (m.quantity || 0) - (m.sold || 0)) })).filter((m) => m.quantity > 0);
  return (
    <div className="fixed inset-0 z-[120] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="photo-verify-modal">
      <div className="bg-white rounded-3xl w-full max-w-lg max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="bg-black px-5 py-4 flex items-center gap-3 rounded-t-3xl">
          <div className="w-10 h-10 bg-amber-400 rounded-xl flex items-center justify-center"><Camera className="w-5 h-5 text-black" /></div>
          <div className="flex-1">
            <p className="text-white font-black">{kind === "reception" ? "Vérification du stock par photo" : "Rapport — stock non vendu"}</p>
            <p className="text-zinc-400 text-[11px]">{user.name} · Code {user.code} · Secteur {String(user.country || "").toUpperCase()}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-zinc-800" data-testid="photo-verify-close"><X className="w-5 h-5 text-white" /></button>
        </div>
        <div className="p-5 space-y-4">
          {!st ? <Loader2 className="w-6 h-6 animate-spin mx-auto text-amber-500" /> : pending && !result ? (
            st.last_reception?.match && a?.status === "verified" ? <Result r={st.last_reception} /> : (
              <div className="bg-amber-50 border-2 border-amber-400 rounded-2xl p-5 text-center" data-testid="verify-pending-msg">
                <p className="text-amber-900 font-black">⏳ Votre stock est en attente — revenez dans quelques heures pour la vérification.</p>
              </div>)
          ) : result ? <Result r={result} /> : busy ? (
            <div className="bg-zinc-950 rounded-2xl p-5" data-testid="verify-analyzing">
              <div className="flex items-center gap-3 mb-4">
                <Loader2 className="w-6 h-6 animate-spin text-amber-400" />
                <div className="flex-1"><p className="text-white font-black text-sm">Analyse de la photo en cours…</p>
                  <p className="text-zinc-400 text-[11px]">Secteur {String(user.country || "").toUpperCase()} · Code {user.code}</p></div>
                <span className="text-amber-400 font-mono font-black">{elapsed}s</span>
              </div>
              <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden mb-4"><div className="h-full bg-amber-400 transition-[width] duration-1000" style={{ width: `${Math.min(95, elapsed * 9)}%` }} /></div>
              <div className="grid grid-cols-3 gap-2">
                {expectedItems.map((it) => (
                  <div key={it.model} className="bg-zinc-900 rounded-xl p-2 text-center animate-pulse">
                    {imageFor(it.model) ? <img src={imageFor(it.model)} alt="" className="w-full aspect-square object-contain bg-white rounded-lg mb-1" /> : <div className="aspect-square flex items-center justify-center text-3xl">💨</div>}
                    <p className="text-white text-[11px] font-bold truncate">{it.model}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <>
              <div>
                <p className="text-zinc-500 text-[10px] font-black uppercase tracking-widest mb-2">{kind === "reception" ? "Stock enregistré par le CEO" : `Stock non vendu à signaler : ${st.unsold_quantity}`}</p>
                <div className="flex flex-wrap gap-2">
                  {expectedItems.map((it) => (
                    <div key={it.model} className="flex items-center gap-2 bg-zinc-100 rounded-xl px-2 py-1.5">
                      {imageFor(it.model) && <img src={imageFor(it.model)} alt="" className="w-8 h-8 object-contain bg-white rounded" />}
                      <span className="text-zinc-900 text-xs font-bold">{it.model}</span>
                    </div>
                  ))}
                </div>
              </div>
              <input ref={ref} type="file" accept="image/*" capture="environment" onChange={pick} className="hidden" data-testid="photo-verify-input" />
              <button onClick={() => ref.current?.click()} className="w-full border-2 border-dashed border-zinc-300 hover:border-amber-400 rounded-2xl overflow-hidden bg-zinc-50" data-testid="photo-verify-take">
                {preview ? <img src={preview} alt="" className="w-full max-h-72 object-contain" /> : (
                  <div className="py-10 flex flex-col items-center gap-2"><Camera className="w-10 h-10 text-zinc-400" /><span className="text-zinc-600 text-sm font-bold">Prendre la photo du stock</span></div>)}
              </button>
              <button onClick={submit} disabled={!file} className="w-full bg-black hover:bg-zinc-800 disabled:opacity-40 text-amber-400 font-black py-3.5 rounded-2xl" data-testid="photo-verify-submit">
                {kind === "reception" ? "Confirmer la réception du stock" : "Envoyer le rapport (en attente de vérification)"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Camera icon for the top bar (next to tracking).
export const PhotoVerifyButton = ({ user }) => {
  const [open, setOpen] = useState(false);
  const [st, reload] = useVerifyStatus(user?.id);
  const verified = st?.assignment?.status === "verified";
  const waiting = st?.assignment?.status === "pending";
  return (
    <>
      <button onClick={() => setOpen(true)} title="Vérification de stock par photo" data-testid="topbar-photo-verify"
        className={`relative flex items-center gap-1.5 font-black px-3 py-2 rounded-xl text-[11px] uppercase tracking-widest shadow ${verified ? "bg-emerald-500 text-white" : "bg-white text-black hover:bg-amber-300"}`}>
        <Camera className="w-4 h-4" /><span className="hidden md:inline">{verified ? "Vérifié" : "Photo stock"}</span>
        {waiting && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full animate-ping" />}
      </button>
      {open && <PhotoVerifyModal user={user} onClose={() => { setOpen(false); reload(); }} />}
    </>
  );
};

// Blinking unsold-stock alert at end of control cycle.
export const UnsoldStockAlert = ({ user }) => {
  const [st, reload] = useVerifyStatus(user?.id);
  const [open, setOpen] = useState(false);
  const { imageFor } = useProductCatalog();
  if (!st?.unsold_quantity) return null;
  const rep = st.last_unsold_report;
  const done = st.unsold_done;
  const left = (user.vape_models || []).map((m) => ({ name: m.name, q: Math.max(0, (m.quantity || 0) - (m.sold || 0)) })).filter((m) => m.q > 0);
  if (done) return (
    <div className="mb-5 bg-white border-2 border-red-500 rounded-2xl p-4" data-testid="unsold-report-done">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="text-5xl font-black text-red-600" data-testid="unsold-qty-red">{st.unsold_quantity}</div>
        <div className="flex-1 min-w-[180px]">
          <p className="text-red-700 font-black">Restant à vendre — Rapport vérifié ✓</p>
          <p className="text-zinc-600 text-xs">{done.validated_by_ceo ? "Validé par le CEO" : "Confirmé par photo"} le {fdate(done.created_at)}</p>
        </div>
        {done.photo_url && <img src={abs(done.photo_url)} alt="" className="w-20 h-20 object-cover rounded-xl ring-4 ring-red-500" data-testid="unsold-report-photo" />}
      </div>
      <div className="flex gap-2 flex-wrap mt-3">
        {left.map((m) => (
          <div key={m.name} className="flex items-center gap-2 bg-red-50 border-2 border-red-500 rounded-xl px-2 py-1.5" data-testid={`unsold-model-${m.name}`}>
            {imageFor(m.name) && <img src={imageFor(m.name)} alt="" className="w-9 h-9 object-contain bg-white rounded" />}
            <span className="text-red-700 text-xs font-black">{m.name} × {m.q}</span>
          </div>
        ))}
      </div>
    </div>
  );
  if (st.unsold_blocked) return (
    <div className="mb-5 bg-black border-2 border-red-500 rounded-2xl p-4 flex items-center gap-4 flex-wrap" data-testid="unsold-blocked">
      <div className="text-5xl font-black text-red-500">{st.unsold_quantity}</div>
      <div className="flex-1 min-w-[180px]">
        <p className="text-white font-black">Produit non identifié après 3 tentatives</p>
        <p className="text-zinc-400 text-xs">Contactez l'équipe Vape Yard pour finaliser votre rapport.</p>
      </div>
      <a href={`https://wa.me/${st.ceo_whatsapp}?text=${encodeURIComponent(`Bonjour équipe Vape Yard, je suis ${user.name} (Code ${user.code}). Mon rapport photo du stock non vendu (${st.unsold_quantity} pièces) n'a pas pu être vérifié.`)}`}
        target="_blank" rel="noreferrer" className="bg-emerald-500 hover:bg-emerald-400 text-white font-black text-xs px-4 py-2.5 rounded-xl" data-testid="unsold-whatsapp-ceo">WhatsApp CEO</a>
    </div>
  );
  return (
    <div className="mb-5" data-testid="unsold-stock-alert">
      <style>{`@keyframes vyBlink{0%,100%{opacity:1}50%{opacity:.15}}`}</style>
      <div className="bg-black rounded-2xl p-4 flex items-center gap-4 flex-wrap border-2 border-red-500">
        <div className="text-5xl font-black text-red-500" style={{ animation: "vyBlink 1s steps(2,start) infinite" }} data-testid="unsold-qty-blink">{st.unsold_quantity}</div>
        <div className="flex-1 min-w-[180px]">
          <p className="text-white font-black">Pièce(s) non vendue(s) à la fin du cycle</p>
          <p className="text-zinc-400 text-xs">Photographiez la quantité restante ({st.unsold_quantity} pièce(s)) — elle sera comparée aux photos des produits enregistrés.</p>
          {st.unsold_failed_attempts > 0 && <p className="text-amber-400 text-xs font-bold mt-1" data-testid="unsold-attempts">Tentative(s) échouée(s) : {st.unsold_failed_attempts}/3</p>}
          {rep && <p className={`text-xs font-bold mt-1 ${rep.match ? "text-emerald-400" : "text-amber-400"}`}>Dernier rapport : {rep.match ? `Vérifié ✓ (${rep.detected_total})` : `Écart — ${rep.detected_total}/${rep.expected_total}, en vérification CEO`} · {fdate(rep.created_at)}</p>}
        </div>
        <button onClick={() => setOpen(true)} className="bg-red-600 hover:bg-red-500 text-white font-black text-xs px-4 py-2.5 rounded-xl flex items-center gap-2" data-testid="unsold-report-btn"><Camera className="w-4 h-4" /> Signaler (Rapport)</button>
      </div>
      {open && <PhotoVerifyModal user={user} kind="unsold" onClose={() => { setOpen(false); reload(); }} />}
    </div>
  );
};

// ================= CEO PANEL =================
const CatalogManager = ({ items, reload }) => {
  const [name, setName] = useState(""); const [price, setPrice] = useState(""); const [file, setFile] = useState(null); const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!name.trim() || !file) return toast.error("Nom et photo requis");
    const fd = new FormData(); fd.append("name", name.trim()); fd.append("price", price || "0"); fd.append("file", file);
    setBusy(true);
    try { await axios.post(`${API}/ceo/stock-verify/catalog`, fd); toast.success("Modèle enregistré"); setName(""); setPrice(""); setFile(null); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };
  const del = async (id) => { if (!window.confirm("Supprimer ce modèle ?")) return; await axios.delete(`${API}/ceo/product-images/${id}`); reload(); };
  return (
    <div className="bg-white border border-zinc-200 rounded-2xl p-4" data-testid="sv-catalog">
      <p className="text-zinc-900 font-black mb-3">A. Catalogue des modèles</p>
      <div className="flex gap-2 flex-wrap mb-3">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nom du modèle" className="flex-1 min-w-[140px] border border-zinc-300 rounded-lg px-3 py-2 text-sm text-zinc-900" data-testid="sv-model-name" />
        <input value={price} onChange={(e) => setPrice(e.target.value)} type="number" placeholder="Prix $" className="w-24 border border-zinc-300 rounded-lg px-3 py-2 text-sm text-zinc-900" data-testid="sv-model-price" />
        <label className="border border-zinc-300 rounded-lg px-3 py-2 text-sm cursor-pointer flex items-center gap-1 text-zinc-700 hover:bg-zinc-50"><Upload className="w-4 h-4" />{file ? file.name.slice(0, 14) : "Photo"}<input type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0])} data-testid="sv-model-file" /></label>
        <button onClick={save} disabled={busy} className="bg-black text-amber-400 font-black text-sm px-4 py-2 rounded-lg" data-testid="sv-model-save">{busy ? "…" : "Enregistrer"}</button>
      </div>
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
        {items.map((m) => (
          <div key={m.id} className="relative bg-zinc-50 border border-zinc-200 rounded-xl p-2 text-center" data-testid={`sv-model-${m.id}`}>
            <img src={abs(m.image_url)} alt={m.name} className="w-full aspect-square object-contain bg-white rounded-lg" />
            <p className="text-zinc-900 text-[11px] font-bold truncate mt-1">{m.name}</p>
            <p className="text-amber-700 text-[10px] font-black">${m.price || 0}</p>
            <button onClick={() => del(m.id)} className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1"><Trash2 className="w-3 h-3" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

const AssignStock = ({ catalog, onSaved }) => {
  const [users, setUsers] = useState([]); const [uid, setUid] = useState(""); const [rows, setRows] = useState([{ model: "", quantity: 1 }]);
  useEffect(() => {
    Promise.all([axios.get(`${API}/stockers`), axios.get(`${API}/network-marketers`)]).then(([a, b]) => {
      const s = Array.isArray(a.data) ? a.data : a.data.stockers || []; const n = Array.isArray(b.data) ? b.data : b.data.marketers || b.data.network_marketers || [];
      setUsers([...s.map((x) => ({ ...x, r: "Stocker" })), ...n.map((x) => ({ ...x, r: "NM" }))]);
    }).catch(() => {});
  }, []);
  const save = async () => {
    try { await axios.post(`${API}/ceo/stock-verify/assign/${uid}`, { items: rows }); toast.success("Stock enregistré — le stocker peut vérifier"); setRows([{ model: "", quantity: 1 }]); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  return (
    <div className="bg-white border border-zinc-200 rounded-2xl p-4" data-testid="sv-assign">
      <p className="text-zinc-900 font-black mb-3">B. Enregistrer le stock attribué</p>
      <select value={uid} onChange={(e) => setUid(e.target.value)} className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm text-zinc-900 mb-2" data-testid="sv-assign-user">
        <option value="">— Choisir un stocker / NM —</option>
        {users.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.code} · {u.country} ({u.r})</option>)}
      </select>
      {rows.map((r, i) => (
        <div key={i} className="flex gap-2 mb-2">
          <select value={r.model} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, model: e.target.value } : x))} className="flex-1 border border-zinc-300 rounded-lg px-3 py-2 text-sm text-zinc-900" data-testid={`sv-assign-model-${i}`}>
            <option value="">— Modèle —</option>
            {catalog.map((m) => <option key={m.id} value={m.name}>{m.name}</option>)}
          </select>
          <input type="number" min="1" value={r.quantity} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, quantity: parseInt(e.target.value || "0", 10) } : x))} className="w-20 border border-zinc-300 rounded-lg px-2 py-2 text-sm text-zinc-900 text-center" data-testid={`sv-assign-qty-${i}`} />
          <button onClick={() => setRows(rows.filter((_, j) => j !== i))} className="px-2 text-red-600"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      <div className="flex gap-2">
        <button onClick={() => setRows([...rows, { model: "", quantity: 1 }])} className="border border-zinc-300 rounded-lg px-3 py-2 text-sm text-zinc-700 flex items-center gap-1" data-testid="sv-assign-add-row"><Plus className="w-4 h-4" />Modèle</button>
        <button onClick={save} disabled={!uid} className="flex-1 bg-black disabled:opacity-40 text-amber-400 font-black text-sm rounded-lg" data-testid="sv-assign-save">Enregistrer le stock</button>
      </div>
    </div>
  );
};

const UnsoldPending = () => {
  const [items, setItems] = useState([]);
  const load = () => axios.get(`${API}/ceo/stock-verify/unsold-pending`).then((r) => setItems(r.data.items || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const validate = async (u) => {
    if (!window.confirm(`Valider le rapport de ${u.name} (${u.unsold_quantity} pièces) à sa place ?`)) return;
    try { await axios.post(`${API}/ceo/stock-verify/validate-unsold/${u.user_id}`); toast.success("Rapport validé"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  return (
    <div className="bg-white border border-zinc-200 rounded-2xl p-4" data-testid="sv-unsold-pending">
      <p className="text-zinc-900 font-black mb-3">E. Rapports stock non vendu en attente</p>
      {!items.length && <p className="text-zinc-500 text-sm">Aucun rapport en attente.</p>}
      <div className="space-y-2">
        {items.map((u) => (
          <div key={u.user_id} className="flex items-center gap-3 bg-red-50 border border-red-300 rounded-xl p-2" data-testid={`sv-unsold-${u.user_id}`}>
            <span className="text-red-600 font-black text-2xl w-12 text-center">{u.unsold_quantity}</span>
            <div className="flex-1"><p className="text-zinc-900 font-bold text-sm">{u.name}</p><p className="text-zinc-500 text-[11px]">{u.code} · {String(u.country || "").toUpperCase()} · échecs {u.failed_attempts}/3</p></div>
            <button onClick={() => validate(u)} className="bg-black text-amber-400 font-black text-xs px-3 py-2 rounded-lg" data-testid={`sv-validate-${u.user_id}`}>Validation rapport</button>
          </div>
        ))}
      </div>
    </div>
  );
};

const Receptions = ({ items, assignments }) => (
  <div className="bg-white border border-zinc-200 rounded-2xl p-4" data-testid="sv-receptions">
    <p className="text-zinc-900 font-black mb-3">D. Réceptions de stock & rapports</p>
    {assignments.filter((a) => a.status === "pending").length > 0 && (
      <p className="text-amber-700 text-xs mb-2">En attente de photo : {assignments.filter((a) => a.status === "pending").map((a) => `${a.user_name} (${a.total})`).join(", ")}</p>
    )}
    <div className="space-y-2">
      {items.length === 0 && <p className="text-zinc-500 text-sm">Aucune réception pour le moment.</p>}
      {items.map((r) => (
        <div key={r.id} className={`flex gap-3 items-center rounded-xl p-2 border ${r.match ? "border-emerald-200 bg-emerald-50" : "border-rose-300 bg-rose-50"}`} data-testid={`sv-reception-${r.id}`}>
          {r.photo_url ? <a href={abs(r.photo_url)} target="_blank" rel="noreferrer"><img src={abs(r.photo_url)} alt="" className={`w-16 h-16 object-cover rounded-lg ${r.kind === "unsold" ? "ring-4 ring-red-500" : ""}`} /></a>
            : <div className="w-16 h-16 rounded-lg bg-zinc-200 flex items-center justify-center text-[10px] text-zinc-600 font-bold text-center">Validé CEO</div>}
          <div className="flex-1 min-w-0">
            <p className="text-zinc-900 font-black text-sm">{r.user_name} <span className="text-zinc-500 text-[11px] font-normal">· {r.code} · {String(r.country || "").toUpperCase()} · {r.kind === "unsold" ? "Non vendu" : "Réception"}</span></p>
            <p className="text-zinc-700 text-xs truncate">{(r.detected || []).map((d) => `${d.model} × ${d.quantity}`).join(" · ") || "—"}</p>
            <p className="text-zinc-500 text-[11px]">{fdate(r.created_at)}</p>
          </div>
          <span className={`text-xs font-black px-2 py-1 rounded-lg ${r.match ? "bg-emerald-600 text-white" : "bg-rose-600 text-white"}`}>{r.match ? `${r.detected_total} — Vérifiée ✓` : `Écart ${r.detected_total}/${r.expected_total}`}</span>
        </div>
      ))}
    </div>
  </div>
);

export const CeoStockVerifyPanel = () => {
  const [catalog, setCatalog] = useState([]); const [rec, setRec] = useState([]); const [asg, setAsg] = useState([]);
  const load = () => {
    axios.get(`${API}/stock-verify/catalog`).then((r) => setCatalog(r.data.items || []));
    axios.get(`${API}/ceo/stock-verify/receptions`).then((r) => setRec(r.data.items || []));
    axios.get(`${API}/ceo/stock-verify/assignments`).then((r) => setAsg(r.data.items || []));
  };
  useEffect(load, []);
  return (
    <div className="space-y-4" data-testid="ceo-stock-verify-panel">
      <CatalogManager items={catalog} reload={load} />
      <AssignStock catalog={catalog} onSaved={load} />
      <UnsoldPending />
      <Receptions items={rec} assignments={asg} />
    </div>
  );
};
